import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer, FileSpreadsheet } from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const months = [
  "Select",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function fmt2(v) {
  const n = Number((v ?? "").toString().replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const SalaryCommitmentReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchtype") || "";

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("Select");

  const [monthName, setMonthName] = useState("Select");
  const [year, setYear] = useState("Select");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // totals (footer)
  const [totals, setTotals] = useState({
    Working: 0,
    Leave: 0,
    Totalnet: 0,
    HRA: 0,
    Conveyance: 0,
    TTWAllowance: 0,
    Totalallowance: 0,
    Total: 0,
    PreVious: 0,
    Bata: 0,
  });

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-??", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const years = useMemo(() => {
    const list = ["Select"];
    const now = new Date().getFullYear();
    for (let y = 2000; y <= now + 5; y++) list.push(String(y));
    return list;
  }, []);

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/salary-commitment-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        const defId = res.data.defaultBranchId || 0;
        setBranchId(defId ? String(defId) : "Select");
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (monthName === "Select") {
      toast.warn("Please select Month");
      return;
    }
    if (year === "Select") {
      toast.warn("Please select Year");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-commitment-report/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId,
          monthName,
          year,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setTotals(res.data.totals || {});
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
        setRows([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Load error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // defaults to current month/year like ASP
    const now = new Date();
    setMonthName(months[now.getMonth() + 1]); // months[0]=Select
    setYear(String(now.getFullYear()));

    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // auto load once meta + defaults are ready
    if (monthName !== "Select" && year !== "Select") fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handlePrint = () => {
    if (monthName === "Select") return toast.warn("Please select Month");
    if (year === "Select") return toast.warn("Please select Year");

    const url =
      `/PayRoll/SalaryCommitmentReportPrint` +
      `?finYearId=${encodeURIComponent(finYearId)}` +
      `&branchId=${encodeURIComponent(branchId)}` +
      `&monthName=${encodeURIComponent(monthName)}` +
      `&year=${encodeURIComponent(year)}`;

    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  };

  const handleExcel = () => {
    toast.info("Excel export will be added after print (same like ASP btnExcel).");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALARY COMMITMENT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Salary Commitment
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Branch / Month / Year → Commitment Summary
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleExcel} className={ghostBtn}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>

              <button onClick={handlePrint} className={primaryBtn}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelBase}>Branch</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={b.fld_branchid} value={b.fld_branchid}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Month *</label>
                <select value={monthName} onChange={(e) => setMonthName(e.target.value)} className={inputBase}>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Year *</label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 flex justify-center">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-3 py-4 text-center font-bold">S.No</th>
                    <th className="px-3 py-4 text-left font-bold">Location</th>
                    <th className="px-3 py-4 text-right font-bold">Net Working Rs</th>
                    <th className="px-3 py-4 text-right font-bold">Net Leave Rs</th>
                    <th className="px-3 py-4 text-right font-bold">Net Total Rs</th>
                    <th className="px-3 py-4 text-right font-bold">Spl HRA</th>
                    <th className="px-3 py-4 text-right font-bold">Conveyance</th>
                    <th className="px-3 py-4 text-right font-bold">TTWAllowance</th>
                    <th className="px-3 py-4 text-right font-bold">Total</th>
                    <th className="px-3 py-4 text-right font-bold">NetSalary+Allowance</th>
                    <th className="px-3 py-4 text-right font-bold">Previous Month Salary</th>
                    <th className="px-3 py-4 text-right font-bold">Bata</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 12 }).map((__, c) => (
                          <td key={c} className="px-3 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-3 py-3 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname}
                        </td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Working)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Leave)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Totalnet)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.HRA)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Conveyance)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.TTWAllowance)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Totalallowance)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Total)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.PreVious)}</td>
                        <td className={`px-3 py-3 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>{fmt2(r.Bata)}</td>
                      </tr>
                    ))
                  )}

                  {/* Footer totals like ASP */}
                  {!loading && rows.length > 0 && (
                    <tr className={`${isDark ? "bg-gray-950" : "bg-gray-100"} font-extrabold`}>
                      <td className="px-3 py-3" />
                      <td className="px-3 py-3 text-left">Total</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Working)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Leave)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Totalnet)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.HRA)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Conveyance)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.TTWAllowance)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Totalallowance)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Total)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.PreVious)}</td>
                      <td className="px-3 py-3 text-right">{fmt2(totals.Bata)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* page change fetch */}
          <PageWatcher page={page} onChange={() => fetchList(page)} />
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

// helper to trigger fetch when page changes
const PageWatcher = ({ page, onChange }) => {
  useEffect(() => {
    onChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  return null;
};

export default SalaryCommitmentReport;
